import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import AppLinkRedirect from "../components/AppLinkRedirect";
import {
  buildDeepLink,
  decodeAndValidatePublicId
} from "../utils/appLinks";

const TITLE = "Watch a reel on Mondoway";
const DESCRIPTION = "Open this reel in the Mondoway app.";
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.dev.mondoway.com"
).replace(/\/$/, "");

function recordShareClick(shareToken) {
  const clickUrl = `${API_BASE_URL}/v1/user-app/social/reels/shares/${encodeURIComponent(
    shareToken
  )}/click`;

  fetch(clickUrl, {
    method: "POST",
    credentials: "omit",
    keepalive: true
  }).catch(() => {
    // Click attribution must never block or break the reel handoff.
  });
}

function setMetaAttribute(selector, value) {
  let element = document.head.querySelector(selector);
  const wasCreated = !element;
  const previousContent = element?.getAttribute("content");

  if (!element) {
    element = document.createElement("meta");
    const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
    element.setAttribute(selector.includes("property=") ? "property" : "name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", value);
  return { element, previousContent, wasCreated };
}

function ReelRedirect() {
  const { reelId: routeReelId = "" } = useParams();
  const reelId = decodeAndValidatePublicId(routeReelId);
  const lastRequestedShareClick = useRef(null);

  useEffect(() => {
    if (!reelId) {
      return;
    }

    const shareToken = new URLSearchParams(window.location.search).get("share");
    const requestKey = `${reelId}:${shareToken}`;

    if (!shareToken || lastRequestedShareClick.current === requestKey) {
      return;
    }

    lastRequestedShareClick.current = requestKey;
    recordShareClick(shareToken);
  }, [reelId]);

  useEffect(() => {
    const previousTitle = document.title;
    const metadata = [
      ['meta[name="description"]', DESCRIPTION],
      ['meta[property="og:title"]', TITLE],
      ['meta[property="og:description"]', DESCRIPTION],
      ['meta[property="og:type"]', "video.other"],
      ['meta[property="og:url"]', window.location.href],
      ['meta[name="twitter:card"]', "summary"],
      ['meta[name="twitter:title"]', TITLE],
      ['meta[name="twitter:description"]', DESCRIPTION]
    ].map(([selector, value]) => setMetaAttribute(selector, value));

    document.title = `${TITLE} | Mondoway`;

    return () => {
      document.title = previousTitle;
      metadata.forEach(({ element, previousContent, wasCreated }) => {
        if (wasCreated) {
          element.remove();
        } else if (previousContent === null) {
          element.removeAttribute("content");
        } else {
          element.setAttribute("content", previousContent);
        }
      });
    };
  }, []);

  return (
    <AppLinkRedirect
      deepLinkUrl={reelId ? buildDeepLink("reel", reelId) : null}
      invalid={!reelId}
      openingTitle="Opening this reel in Mondoway..."
      invalidTitle="This reel link is invalid."
    />
  );
}

export default ReelRedirect;
